import { DigitalSignStepEnum } from "domain/enum/digitalSignSteps";
import { MessageTypeEnum } from "domain/enum/messageType";
import { TripsheetStatusEnum } from "domain/enum/tripsheetStatus";
import { TripsheetInputFilter } from "domain/models/tripsheet-input-filter";
import { Tripsheet } from "domain/entities/tripsheet";
import Spinner from "presentation/components/Shared/Spinner";
import { useDigitalSignApproverByFilter } from "presentation/hooks/use-query-digital-sign-approver";
import { useTripsheetsByFilter } from "presentation/hooks/use-query-tripsheet";
import MobileS3PDFViewerDialog from "presentation/mobile/components/MobileS3PDFViewerDialog";
import { useNavigationStore } from "presentation/mobile/stores/navigation-store";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { Toast } from "primereact/toast";
import { useEffect, useMemo, useRef, useState } from "react";
import useLongPress from "presentation/mobile/hooks/useLongPress";
import MobileFilterTabs, { FilterTabItem } from "presentation/mobile/components/MobileFilterTabs";
import MobileActionButton from "presentation/mobile/components/MobileActionButton";
import MobileToast from "../MobileToast";
import { TRIPSHEET_STATUS_TONES, getStatusStyle } from "presentation/mobile/utils/status-styles.util";

type DocumentFilterTab = "signed" | "pending" | "all";

interface TripsheetDocumentItemProps {
    tripsheet: Tripsheet;
    isExpanded: boolean;
    isLoadingReport: boolean;
    onToggle: () => void;
    onView: (id: string) => void;
}

const TripsheetDocumentItem: React.FC<TripsheetDocumentItemProps> = ({
    tripsheet,
    isExpanded,
    isLoadingReport,
    onToggle,
    onView,
}) => {
    const { isPressing, handlers } = useLongPress({
        onLongPress: onToggle,
    });

     const getStatusLabel = (status: string): string => {
        if (status === TripsheetStatusEnum.ASSINADO_2) return "Assinado";
        if (status === TripsheetStatusEnum.ASSINADO_1) return "Pendente";
        return status ?? "-";
    };

    const statusInfo = {
        label: getStatusLabel(tripsheet.status),
        ...getStatusStyle(TRIPSHEET_STATUS_TONES, tripsheet.status),
    };


    const formatCurrency = (val?: number) => {
        if (val === undefined || val === null) return "-";
        return val.toLocaleString("pt-PT", {
            style: "currency",
            currency: "EUR",
        });
    };

    return (
        <div
            {...handlers}
            className="border-round-xl p-3 flex flex-column gap-2 cursor-pointer"
            style={{
                backgroundColor: "#FFFFFF",
                border: isExpanded ? "1.5px solid #0084D1" : "1px solid #E2E8F0",
                boxShadow: isExpanded ? "0 4px 14px rgba(0, 132, 209, 0.08)" : "none",
                transform: isPressing ? "scale(0.99)" : "scale(1)",
                transition: "all 0.2s ease",
            }}
        >
            {/* Linha 1: Título com ponto azul se expandido + Badge de Estado + Botão '+' / '✕' */}
            <div className="w-full flex justify-content-between align-items-center">
                <div className="flex align-items-center gap-1 overflow-hidden pr-2">
                    {isExpanded && (
                        <span
                            style={{
                                color: "#0084D1",
                                fontSize: "1.1rem",
                                lineHeight: "1",
                                marginRight: "2px",
                            }}
                        >
                            •
                        </span>
                    )}
                    <span className="font-semibold text-sm text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                        {`Mapa de KMs - ${String(tripsheet.month).padStart(2, "0")}/${tripsheet.year}`}
                    </span>
                </div>

                <div className="flex align-items-center gap-2 flex-shrink-0">
                    <span
                        className="badge-squircle"
                        style={{
                            color: statusInfo.color,
                            backgroundColor: statusInfo.bg,
                            border: `1px solid ${statusInfo.border}`,
                        }}
                    >
                        {statusInfo.label}
                    </span>

                    {/* Micro-interação: '+' gira 45º para '✕' */}
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggle();
                        }}
                        className="border-none bg-transparent cursor-pointer flex align-items-center justify-content-center p-0"
                        style={{
                            width: "24px",
                            height: "24px",
                            color: "#94A3B8",
                            outline: "none",
                        }}
                        aria-label={isExpanded ? "Recolher" : "Expandir"}
                    >
                        <i
                            className="pi pi-plus text-xs"
                            style={{
                                transform: isExpanded ? "rotate(45deg)" : "rotate(0deg)",
                                transition: "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                            }}
                        />
                    </button>
                </div>
            </div>

            {/* Linha 2: Versão e Valor Apurado (sem erros de tipo) */}
            <div className="w-full flex justify-content-between align-items-center">
                <span className="text-xs" style={{ color: "#64748B" }}>
                    Versão: <strong style={{ color: "#0F172A" }}>{tripsheet.version ?? 1}</strong>
                </span>
                {tripsheet.kmValueCalculated !== undefined && (
                    <span className="font-bold text-sm" style={{ color : "#0084D1" }}>
                        {formatCurrency(tripsheet.kmValueCalculated)}
                    </span>
                )}
            </div>

            {/* Secção Expandida: Botão Reutilizável de Visualizar */}
            {isExpanded && (
                <div
                    className="w-full flex flex-column gap-2 pt-2 mt-1"
                    style={{ borderTop: "1px solid #F1F5F9" }}
                >
                    <div className="w-full flex justify-content-end gap-2 pt-1">
                        <MobileActionButton
                            label={isLoadingReport ? "A carregar..." : "Visualizar"}
                            icon="pi pi-eye"
                            severity="secondary"
                            onClick={() => tripsheet.id && onView(tripsheet.id)}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

const TripsheetDocumentsList = () => {
    const toast = useRef<Toast>(null);
    const { userEmployee } = useAuthStore();
    const navigationState = useNavigationStore();

    const [tripsheetIdToReport, setTripsheetIdToReport] = useState<string | null>(null);
    const [s3PathToDisplay, setS3PathToDisplay] = useState<string | null>(null);
    const [expandedDocumentId, setExpandedDocumentId] = useState<string | null>(null);
    const [reportRequestedAt, setReportRequestedAt] = useState<number>(0);
    const [activeTab, setActiveTab] = useState<DocumentFilterTab>("all");

    const showMessage = (
        severity: MessageTypeEnum,
        summary: string,
        detail: string
    ) => {
        toast.current?.show({ severity, summary, detail, life: 2500 });
    };

    useEffect(() => {
        if (navigationState.currentRoute !== "documents") {
            navigationState.setCurrentRoute("documents");
        }
    }, [navigationState]);

    const {
        data: tripsheetsData,
        isLoading,
        isError,
        error,
    } = useTripsheetsByFilter({
        employeeId: userEmployee?.id ?? "",
        status: "Assinado 1,Assinado 2",
        sortBy: "createdAt",
        sortOrder: "desc",
    } as TripsheetInputFilter);

    const { 
        data: digitalSignApproverData,
         isFetching: isFetchingApprover,
        dataUpdatedAt: approverDataUpdatedAt, 
    } = useDigitalSignApproverByFilter({
            referenceId: tripsheetIdToReport ?? "",
        });

    useEffect(() => {
        if (!tripsheetIdToReport) return;

        if (isFetchingApprover || approverDataUpdatedAt < reportRequestedAt) return;
        if (digitalSignApproverData?.data) {
            if (digitalSignApproverData.data.length > 0) {
                const approvers = digitalSignApproverData.data;
                const docSignedByEmployee = approvers.find(
                    (item) => item.step === DigitalSignStepEnum.STEP_EMPLOYEE
                );
                const docSignedByCompany = approvers.find(
                    (item) => item.step === DigitalSignStepEnum.STEP_COMPANY
                );

                const s3Path =
                    docSignedByEmployee?.documentPath || docSignedByCompany?.documentPath;

                if (s3Path) {
                    setS3PathToDisplay(s3Path);
                } else {
                    showMessage(
                        MessageTypeEnum.ERROR,
                        "Relatório",
                        "Nenhum caminho de documento encontrado."
                    );
                }
            } else if (!isFetchingApprover) {
                showMessage(
                    MessageTypeEnum.WARN,
                    "Relatório",
                    "Ainda não existe um relatório para este mapa de KMs."
                );
            }

            setTripsheetIdToReport(null);
        }
    }, [digitalSignApproverData, isFetchingApprover, tripsheetIdToReport, approverDataUpdatedAt, reportRequestedAt]);

    useEffect(() => {
        if (isError && error) {
            showMessage(
                MessageTypeEnum.ERROR,
                "Erro",
                (error as any)?.message ?? "Erro ao carregar documentos."
            );
        }
    }, [isError, error]);

    const tripsheets: Tripsheet[] = tripsheetsData?.data ?? [];

    const signedCount = useMemo(() => {
        return tripsheets.filter((t) => t.status === TripsheetStatusEnum.ASSINADO_2).length;
    }, [tripsheets]);

    const pendingCount = useMemo(() => {
        return tripsheets.filter((t) => t.status === TripsheetStatusEnum.ASSINADO_1).length;
    }, [tripsheets]);

    const filterTabs: FilterTabItem<DocumentFilterTab>[] = useMemo(() => {
        return [
            { key: "signed", label: "Assinados", count: signedCount },
            { key: "pending", label: "Pendentes", count: pendingCount },
            { key: "all", label: "Todos", count: tripsheets.length },
        ];
    }, [signedCount, pendingCount, tripsheets.length]);

    const filteredTripsheets = useMemo(() => {
        switch (activeTab) {
            case "signed":
                return tripsheets.filter((t) => t.status === TripsheetStatusEnum.ASSINADO_2);
            case "pending":
                return tripsheets.filter((t) => t.status === TripsheetStatusEnum.ASSINADO_1);
            case "all":
            default:
                return tripsheets;
        }
    }, [tripsheets, activeTab]);

    if (isLoading) {
        return (
            <div className="flex justify-content-center align-items-center h-screen">
                <Spinner />
            </div>
        );
    }

    return (
        <div className="flex flex-column pt-2 px-3 gap-2 pb-8 align-items-center">
            <MobileToast ref={toast} />

            {/* Abas de Filtro Reutilizáveis */}
            <MobileFilterTabs
                tabs={filterTabs}
                activeTab={activeTab}
                onChange={setActiveTab}
                scrollable
            />

            {/* Cabeçalho da Secção (Sem CAPSLOCK) */}
            <div className="w-full flex justify-content-between align-items-center pt-1 px-1">
                <span
                    className="font-semibold text-xs"
                    style={{ color: "#64748B" }}
                >
                    Os meus documentos
                </span>
                <span className="text-xs" style={{ color: "#94A3B8" }}>
                    {filteredTripsheets.length} {filteredTripsheets.length === 1 ? "documento" : "documentos"}
                </span>
            </div>

            {/* Lista de Documentos com Expansão Integrada */}
            <div className="w-full flex flex-column justify-content-center gap-3">
                {filteredTripsheets.length === 0 ? (
                    <div className="flex flex-column align-items-center justify-content-center pt-6 gap-2">
                        <i className="pi pi-file text-4xl" style={{ color: "#CBD5E1" }} />
                        <span className="text-sm text-center" style={{ color: "#64748B" }}>
                            {activeTab !== "all"
                                ? "Nenhum documento encontrado com este filtro."
                                : "Não tem documentos disponíveis."}
                        </span>
                    </div>
                ) : (
                    filteredTripsheets.map((tripsheet) => (
                        <TripsheetDocumentItem
                            key={tripsheet.id}
                            tripsheet={tripsheet}
                            isExpanded={expandedDocumentId === tripsheet.id}
                            isLoadingReport={isFetchingApprover && tripsheetIdToReport === tripsheet.id}
                            onToggle={() =>
                                setExpandedDocumentId((prev) =>
                                    prev === tripsheet.id ? null : tripsheet.id ?? null
                                )
                            }
                            onView={(id) => {
                                setReportRequestedAt(Date.now());
                                setTripsheetIdToReport(id)
                            }}
                        />
                    ))
                )}
            </div>

            {/* PDF assinado do S3 */}
            {s3PathToDisplay && (
                <MobileS3PDFViewerDialog
                    s3Path={s3PathToDisplay}
                    onHide={() => setS3PathToDisplay(null)}
                />
            )}
        </div>
    );
};

export default TripsheetDocumentsList;